import * as Toast from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';
import {
  SnackbarContext,
  SnackbarSpaceContext,
  type Snackbar,
  type SnackbarAction,
  type SnackbarTone,
} from './use-snackbar.js';

interface SnackbarMessage {
  id: number;
  tone: SnackbarTone;
  text: string;
  action?: SnackbarAction | undefined;
}

const AUTO_DISMISS_MS = 5000;
const AUTO_DISMISS_WITH_ACTION_MS = 8000;
/// Acima disso a pilha deixa de ser aviso e vira parede: esconde o rodapé da tela justamente
/// quando a pessoa quer voltar a mexer nela.
const MAX_STACKED = 3;

let lastId = 0;

/// O aviso de que uma ação deu certo ou não, ancorado embaixo, no centro da tela.
///
/// Existe porque a resposta ao salvar era uma faixa no topo do formulário, e o botão de salvar fica
/// no fim dele: em tela de celular a confirmação nascia fora do campo de visão, e quem clicava não
/// via nada acontecer. Aqui o aviso aparece onde os olhos já estão — perto do botão que acabou de
/// ser apertado — e não empurra o conteúdo, então a página não dá o pulo que faz perder a linha
/// que se estava lendo.
///
/// O que NÃO vem para cá: erro de campo, que precisa ficar colado no campo errado; e erro de
/// formulário aberto, que precisa continuar legível enquanto a pessoa corrige. Aviso transitório é
/// para o RESULTADO de uma ação; o que exige correção continua inline.
///
/// `scope` é a tela onde a ação aconteceu. Quando muda, o erro vai embora: ele fica até alguém
/// fechar porque explica o que falhou AQUI, e na tela seguinte só confunde — "cadastre o celular do
/// cliente" boiando sobre a lista de clientes parece falar dela. A confirmação segue o relógio,
/// porque salvar e sair logo em seguida é justamente quando ela precisa atravessar a troca.
export function SnackbarProvider({ children, scope }: { children: ReactNode; scope?: string }) {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);
  const [shownScope, setShownScope] = useState(scope);
  if (shownScope !== scope) {
    setShownScope(scope);
    setMessages((current) => current.filter((message) => message.tone !== 'danger'));
  }
  const [space, setSpace] = useState(0);
  // O elemento em estado, e não em `ref`: a medição precisa acontecer de novo quando ele aparece,
  // e uma `ref` mudando de valor não acorda efeito nenhum.
  const [viewport, setViewport] = useState<HTMLOListElement | null>(null);
  // Recipiente próprio, irmão da aplicação no `body`. É o que dá ao aviso um lugar de onde a
  // marcação de diálogo aberto possa ser desfeita sem desfazê-la para a página inteira.
  const [host] = useState(() => document.createElement('div'));

  const show = useCallback(
    (text: string, tone: SnackbarTone = 'positive', action?: SnackbarAction) => {
      lastId += 1;
      // Apertar Salvar duas vezes não empilha o mesmo aviso duas vezes: o repetido toma o lugar do
      // anterior e o relógio recomeça, que é o que a segunda apertada quis dizer.
      setMessages((current) =>
        [
          ...current.filter((message) => message.text !== text),
          { id: lastId, tone, text, action },
        ].slice(-MAX_STACKED),
      );
    },
    [],
  );

  const snackbar = useMemo<Snackbar>(
    () => ({
      show,
      success: (text, action) => show(text, 'positive', action),
      failure: (text) => show(text, 'danger'),
    }),
    [show],
  );

  useEffect(() => {
    if (!viewport) return;
    const measure = () => setSpace(messages.length === 0 ? 0 : viewport.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewport, messages.length]);

  useEffect(() => {
    document.body.append(host);
    // Um diálogo que abre depois marca de novo; o observador desfaz sem esperar a próxima mensagem.
    const guard = new MutationObserver(() => host.removeAttribute('aria-hidden'));
    guard.observe(host, { attributes: true, attributeFilter: ['aria-hidden'] });
    return () => {
      guard.disconnect();
      host.remove();
    };
  }, [host]);

  /// Um diálogo aberto marca `aria-hidden` em tudo que está fora dele, e está certo: quem usa
  /// leitor de tela deve ficar restrito ao diálogo. Errado é a marca pegar o aviso junto —
  /// exatamente quem salvou de dentro do diálogo é quem precisa ouvir se deu certo. A marca é
  /// desfeita só neste recipiente, que não contém nada além dos avisos, e antes da pintura: se
  /// dependesse do observador, a mensagem poderia nascer marcada e nunca ser anunciada.
  useLayoutEffect(() => host.removeAttribute('aria-hidden'), [host, messages]);

  return (
    <SnackbarContext.Provider value={snackbar}>
      <SnackbarSpaceContext.Provider value={space}>
        <Toast.Provider swipeDirection="down" label="Aviso">
          {children}
          {messages.map((message) => (
            <SnackbarItem
              key={message.id}
              message={message}
              onDismiss={() =>
                setMessages((current) => current.filter((other) => other.id !== message.id))
              }
            />
          ))}
          {createPortal(
            <Toast.Viewport
              ref={setViewport}
              label="Avisos ({hotkey})"
              // Acima da camada do modal: salvar de dentro de um diálogo tem de poder responder.
              // Embaixo e no centro: no canto direito o aviso cobria o total e o botão de confirmar
              // dos painéis laterais, como o da Nova venda.
              className="fixed inset-x-0 bottom-0 z-[60] mx-auto mb-0 flex w-full max-w-[26rem] list-none flex-col gap-2 p-4 outline-none sm:p-5"
            />,
            host,
          )}
        </Toast.Provider>
      </SnackbarSpaceContext.Provider>
    </SnackbarContext.Provider>
  );
}

function SnackbarItem({ message, onDismiss }: { message: SnackbarMessage; onDismiss: () => void }) {
  const { icon: Icon, accent, edge } = appearance[message.tone];
  return (
    <Toast.Root
      // O erro não some sozinho. Quem estava olhando outro canto da tela quando a gravação falhou
      // precisa achar a mensagem depois — um erro que se apaga em cinco segundos repete, com outra
      // cara, o problema de não mostrar nada.
      duration={
        message.tone === 'danger'
          ? Number.POSITIVE_INFINITY
          : message.action
            ? AUTO_DISMISS_WITH_ACTION_MS
            : AUTO_DISMISS_MS
      }
      // Só fecha, nunca reabre: este aviso nasce aberto e some da lista quando fecha, então o
      // aviso é a única coisa que a troca de estado pode significar.
      onOpenChange={onDismiss}
      // Fundo opaco, e não um véu da cor do tom: o aviso flutua sobre o conteúdo da página, e
      // tinta translúcida deixaria a letra por cima do texto de baixo.
      className={cn(
        'pointer-events-auto flex w-full items-start gap-3 rounded-xl border bg-surface-elevated px-4 py-3 shadow-card',
        // Entra animado e sai na hora: fechar tira a mensagem da lista, e aí não sobra elemento
        // para animar a saída. O arrasto segue o dedo enquanto ele está na tela.
        'data-[state=open]:animate-[snackbar-in_.16s_ease-out]',
        'data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=cancel]:translate-y-0',
        edge,
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', accent)} aria-hidden="true" />
      <Toast.Description className="min-w-0 flex-1 text-sm leading-5 text-content">
        {message.text}
      </Toast.Description>
      {message.action ? (
        <Toast.Action
          altText={message.action.label}
          onClick={message.action.onAction}
          className="-my-0.5 shrink-0 rounded-lg px-2 py-0.5 text-sm font-semibold text-brand-400 transition hover:bg-brand-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        >
          {message.action.label}
        </Toast.Action>
      ) : null}
      <Toast.Close
        aria-label="Fechar aviso"
        className="-mr-1 -mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg text-content-faint transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
      >
        <X className="size-4" aria-hidden="true" />
      </Toast.Close>
    </Toast.Root>
  );
}

const appearance: Record<
  SnackbarTone,
  { icon: typeof CheckCircle2; accent: string; edge: string }
> = {
  positive: { icon: CheckCircle2, accent: 'text-positive', edge: 'border-positive/40' },
  danger: { icon: AlertTriangle, accent: 'text-danger', edge: 'border-danger/45' },
  info: { icon: Info, accent: 'text-info', edge: 'border-info/40' },
};
